import type { ComponentProps, CSSProperties, PropsWithChildren } from 'react';
import { css, styled } from 'styled-components';
import { theme, usePreviousDistinct } from '@frameio/vapor';
import { useAssetAggregateLayout } from '../../shared';
import {
  useAssetState,
  useOnPulseIteration,
  useUploadProgress,
} from '../contexts';
import type { AssetState } from '../types';
import { AssetCircularProgress } from './AssetCircularProgress';
import { Pulse } from './Pulse';
import { ThumbnailReveal } from './ThumbnailReveal';

type Props = PropsWithChildren<{
  size: ComponentProps<typeof AssetCircularProgress>['size'];
  style?: CSSProperties;
}>;

/**
 * The AssetStateIndicator is a wrapper component for rendering asset states,
 * typically used to wrap a `MediaThumbnail` component.
 */
export function AssetStateIndicator({ size, style, children }: Props) {
  const isList = useAssetAggregateLayout() === 'LIST';
  const assetState = useAssetState();
  const uploadProgress = useUploadProgress();
  const onPulseIteration = useOnPulseIteration();

  const prevAssetState = usePreviousDistinct(assetState);

  const isPreparing =
    assetState === 'preparing' || assetState === 'interactive';
  const isThumbVisible =
    isPreparing || VISIBLE_THUMB_STATES.includes(assetState);

  const wasGeneratingThumbnail =
    prevAssetState === 'generating-thumbnail' && isThumbVisible;
  const wasRemoteGenerating = prevAssetState === 'remote' && isThumbVisible;

  return (
    <RelativeThumbnailContainer $isList={isList} style={style}>
      <Pulse
        isIterating={
          assetState === 'generating-thumbnail' ||
          assetState === 'firefly-generating' ||
          assetState === 'remote'
        }
        onIteration={onPulseIteration}
      >
        <AssetCircularProgress
          assetState={assetState}
          percentage={uploadProgress?.percentage}
          size={size}
        />
      </Pulse>
      <ThumbnailReveal
        isPreparing={isPreparing}
        isThumbVisible={isThumbVisible}
        shouldPlayRevealAnimation={
          wasGeneratingThumbnail || wasRemoteGenerating
        }
      >
        {children}
      </ThumbnailReveal>
    </RelativeThumbnailContainer>
  );
}

const VISIBLE_THUMB_STATES: AssetState[] = [
  'complete',
  'transcribing',
  'transcribing-processing',
  'validating-brand',
];

AssetStateIndicator.displayName = 'AssetStateIndicator';

const RelativeThumbnailContainer = styled.div<{ $isList: boolean }>`
  background: ${theme.colors.background.secondary};
  ${(p) =>
    !p.$isList &&
    css`
      border-top-left-radius: ${theme.radii[8]};
      border-top-right-radius: ${theme.radii[8]};
    `}
  height: 100%;
  position: relative;
  width: 100%;
  z-index: 0;
`;
